import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Mail, Download, FileText, Search, Loader2, Shield, Calendar, Send, X,
  MessageCircle, AlertTriangle,
} from 'lucide-react';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

/**
 * Agent Memos & Notices — read-only inbox of correspondence sent to the
 * current agent. Iter 47 adds an in-thread Reply composer (text + optional
 * file attachment) with append-only history; admin replies appear in the
 * same thread.
 */
const AgentMemosPanel = ({ agent, apiUrl }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [opened, setOpened] = useState(null);  // id of open thread

  const fetchInbox = useCallback(async () => {
    if (!agent?.id) return;
    setLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/correspondence/agent/${agent.id}`);
      if (r.ok) setItems(await r.json());
    } finally { setLoading(false); }
  }, [apiUrl, agent?.id]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return items;
    return items.filter(c =>
      (c.doc_number || '').toLowerCase().includes(q) ||
      (c.subject || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const downloadPdf = async (id, filename) => {
    const r = await fetch(`${apiUrl}/api/correspondence/${id}/pdf?agent_id=${agent.id}`);
    if (!r.ok) { alert('Download failed.'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename || 'correspondence.pdf'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card data-testid="agent-memos-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#0077B6]" /> Memos &amp; Notices
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
            <Shield className="h-3 w-3" /> Audit trail
          </span>
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          Official business communications from Utmost Financial Inc. You can
          reply directly here — every message in the thread is permanently
          recorded for compliance.
        </p>
      </CardHeader>
      <CardContent>
        <div className="mb-3 relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by doc# or subject…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-[#0077B6]"
            data-testid="memos-search"
          />
        </div>

        {loading ? (
          <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <Mail className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No memos have been sent to you yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(c => (
              <div
                key={c.id}
                className={`border rounded-lg p-3 ${c.unread ? 'bg-blue-50/40 border-blue-300' : 'hover:bg-gray-50'}`}
                data-testid={`memo-row-${c.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <FileText className="h-5 w-5 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[11px] text-gray-500">{c.doc_number}</span>
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{c.subject}</h3>
                      {c.unread && (
                        <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">NEW</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                      <span><Calendar className="inline h-3 w-3 mr-1" /> {fmt(c.sent_at || c.created_at)}</span>
                      <span className="text-gray-400">From: {c.created_by_admin_name || 'Utmost Financial Inc.'}</span>
                      {(c.replies_count || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-[#0077B6]">
                          <MessageCircle className="h-3 w-3" /> {c.replies_count} {c.replies_count === 1 ? 'reply' : 'replies'}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => setOpened(c.id)}
                      className="bg-[#0077B6]"
                      data-testid={`memo-open-${c.id}`}
                    >
                      <MessageCircle className="h-4 w-4 mr-1" /> Open
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadPdf(c.id, c.pdf_filename)}
                      data-testid={`memo-download-${c.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {opened && (
        <AgentThreadModal
          correspondenceId={opened}
          apiUrl={apiUrl}
          agent={agent}
          onClose={() => { setOpened(null); fetchInbox(); }}
          onChanged={fetchInbox}
        />
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
function AgentThreadModal({ correspondenceId, apiUrl, agent, onClose, onChanged }) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [replying, setReplying] = useState(false);
  const [err, setErr] = useState('');

  const fetchThread = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/correspondence/${correspondenceId}/thread?agent_id=${agent.id}`);
      if (r.ok) {
        const d = await r.json();
        setThread(d);
        // Mark read for this agent
        if ((d.unread_for || []).includes(agent.id)) {
          fetch(`${apiUrl}/api/correspondence/${correspondenceId}/mark-read?agent_id=${agent.id}`, {
            method: 'POST',
          }).then(() => onChanged && onChanged());
        }
      }
    } finally { setLoading(false); }
  }, [apiUrl, correspondenceId, agent.id, onChanged]);

  useEffect(() => { fetchThread(); }, [fetchThread]);

  const submitReply = async () => {
    setErr('');
    if (!replyText.trim()) { setErr('Reply body is required.'); return; }
    setReplying(true);
    try {
      const fd = new FormData();
      fd.append('body_text', replyText.trim());
      fd.append('author_type', 'agent');
      fd.append('agent_id', agent.id);
      fd.append('author_name', agent.name || '');
      if (replyFile) fd.append('attachment', replyFile);
      const r = await fetch(`${apiUrl}/api/correspondence/${correspondenceId}/reply`, {
        method: 'POST', body: fd,
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Reply failed');
      }
      setReplyText('');
      setReplyFile(null);
      await fetchThread();
      onChanged && onChanged();
    } catch (e) { setErr(e.message); } finally { setReplying(false); }
  };

  const downloadAttachment = async (replyId, filename) => {
    const r = await fetch(
      `${apiUrl}/api/correspondence/${correspondenceId}/reply-attachment/${replyId}?agent_id=${agent.id}`
    );
    if (!r.ok) { alert('Download failed'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename || 'attachment'; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    if (!thread) return;
    const r = await fetch(`${apiUrl}/api/correspondence/${correspondenceId}/pdf?agent_id=${agent.id}`);
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = thread.pdf_filename || 'memo.pdf'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="agent-thread-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white">
          <div className="min-w-0">
            <p className="font-mono text-xs text-gray-500">{thread?.doc_number}</p>
            <h2 className="text-lg font-semibold truncate">{thread?.subject || 'Loading…'}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          {loading || !thread ? (
            <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
          ) : (
            <>
              <div className="text-xs text-gray-500 flex flex-wrap gap-3">
                <span><Calendar className="inline h-3 w-3 mr-1" />Sent: {fmt(thread.sent_at || thread.created_at)}</span>
                <span>From: <b className="text-gray-700">{thread.created_by_admin_name || 'Utmost Financial Inc.'}</b></span>
              </div>

              {thread.body_text && (
                <div className="bg-gray-50 border rounded p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-1">Original Memo</p>
                  <pre className="whitespace-pre-wrap font-serif text-sm text-gray-900 max-h-40 overflow-y-auto">{thread.body_text}</pre>
                </div>
              )}

              <Button variant="outline" size="sm" onClick={downloadPdf} className="text-xs" data-testid="agent-thread-download-pdf">
                <Download className="h-3.5 w-3.5 mr-1" /> Download official PDF
              </Button>

              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  Conversation ({(thread.replies || []).length} {(thread.replies || []).length === 1 ? 'reply' : 'replies'})
                </p>
                {(thread.replies || []).length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No replies yet. Be the first to respond below.</p>
                ) : (
                  <div className="space-y-2" data-testid="agent-thread-replies">
                    {thread.replies.map(rep => (
                      <div
                        key={rep.id}
                        className={`p-3 rounded-lg border ${rep.author_type === 'admin' ? 'bg-amber-50/40 border-amber-200' : 'bg-blue-50/40 border-blue-200'}`}
                        data-testid={`thread-reply-${rep.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-semibold text-gray-800">
                            {rep.author_name}
                            <span className="text-[10px] uppercase ml-1 px-1.5 py-0.5 rounded bg-white border">{rep.author_type}</span>
                          </span>
                          <span className="text-[11px] text-gray-500">{fmt(rep.sent_at)}</span>
                        </div>
                        <pre className="whitespace-pre-wrap font-sans text-sm text-gray-900">{rep.body_text}</pre>
                        {rep.attachment && (
                          <button
                            onClick={() => downloadAttachment(rep.id, rep.attachment.filename)}
                            className="text-xs text-[#0077B6] hover:underline mt-2 inline-flex items-center gap-1"
                            data-testid={`thread-reply-attachment-${rep.id}`}
                          >
                            <Download className="h-3 w-3" /> {rep.attachment.filename}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t pt-3" data-testid="agent-reply-composer">
                <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                  <Send className="h-3.5 w-3.5" /> Your reply
                </p>
                {err && (
                  <div className="bg-red-50 border-l-4 border-red-500 text-red-900 p-2 rounded text-xs mb-2 flex items-start gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {err}
                  </div>
                )}
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={3}
                  placeholder="Type your reply…"
                  className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
                  data-testid="agent-reply-text"
                />
                <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
                  <input
                    type="file"
                    onChange={(e) => setReplyFile(e.target.files?.[0] || null)}
                    className="text-xs"
                    data-testid="agent-reply-attachment"
                  />
                  <Button onClick={submitReply} disabled={replying} className="bg-[#0077B6]" size="sm" data-testid="agent-reply-send-btn">
                    {replying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                    Post Reply
                  </Button>
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  Replies are permanently recorded as part of the official correspondence.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AgentMemosPanel;
